import * as JSZipModule from "jszip";

interface ZipEntry {
  async(type: "string"): Promise<string>;
}

interface MutableZip {
  files: Record<string, unknown>;
  file(path: string): ZipEntry | null;
  file(path: string, contents: string): unknown;
  generateAsync(options: { type: "base64"; compression: "DEFLATE" }): Promise<string>;
}

interface MinimalJSZip {
  loadAsync(data: string, options: { base64: boolean }): Promise<MutableZip>;
}

export const JSZip = ((JSZipModule as unknown as { default?: MinimalJSZip }).default ??
  (JSZipModule as unknown as MinimalJSZip));

export const CHART_PART = /^xl\/charts\/chart\d+\.xml$/;
const CHART_GROUP =
  /<c:(lineChart|line3DChart|barChart|bar3DChart|areaChart|area3DChart|scatterChart|stockChart|bubbleChart|radarChart)>([\s\S]*?)<\/c:\1>/g;
const LINE_GROUPS = new Set(["lineChart", "line3DChart", "scatterChart", "stockChart"]);
const AREA_REFERENCE =
  /(?:'((?:[^']|'')+)'|([A-Za-z_][\w.]*))!\$?([A-Z]{1,3})\$?(\d+)(?::\$?([A-Z]{1,3})\$?(\d+))?/g;
const NON_NUMERIC_CELL_TYPES = new Set(["s", "str", "inlineStr", "e", "b"]);
/** Number formats without a percent, currency or text, so the axis step's decimals can stand in for them. */
const PLAIN_NUMBER_FORMAT = /^(?:General|[-_*\s#,0.;\\"?@()]+)$/;

export interface ValueAxisBounds {
  min: number;
  max: number;
}

export interface ValueAxisScale extends ValueAxisBounds {
  step: number;
}

export interface RescaledValueAxis {
  chartPath: string;
  from: ValueAxisBounds;
  to: ValueAxisBounds;
}

export interface ValueAxis {
  id: string;
  vertical: boolean;
  bounds?: ValueAxisBounds;
  start: number;
  end: number;
  xml: string;
}

interface Area {
  sheet: string;
  firstColumn: number;
  lastColumn: number;
  firstRow: number;
  lastRow: number;
}

interface Cell {
  column: number;
  row: number;
  /** NaN for a filled cell that holds text, an error or a boolean. */
  value: number;
}

function contains(area: Area, cell: Cell): boolean {
  return (
    cell.column >= area.firstColumn &&
    cell.column <= area.lastColumn &&
    cell.row >= area.firstRow &&
    cell.row <= area.lastRow
  );
}

export function decodeXml(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function readAttribute(xml: string, name: string): string {
  const match = new RegExp(`\\b${name}="([^"]*)"`, "i").exec(xml);
  return match ? decodeXml(match[1]) : "";
}

export function readValue(xml: string, tag: string): string | undefined {
  return new RegExp(`<c:${tag} val="([^"]*)"`).exec(xml)?.[1];
}

function columnNumber(letters: string): number {
  let column = 0;
  for (const letter of letters.toUpperCase()) column = column * 26 + letter.charCodeAt(0) - 64;
  return column;
}

function resolvePartPath(basePart: string, target: string): string {
  if (target.startsWith("/")) return target.replace(/^\/+/, "");
  const segments = basePart.split("/");
  segments.pop();
  for (const segment of target.split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") segments.pop();
    else segments.push(segment);
  }
  return segments.join("/");
}

/**
 * The smallest 1, 2 or 5 × 10ⁿ step of at least a tenth of the data range,
 * with the data's extremes rounded out to that step. This reproduces the fixed
 * bounds ChartCreator writes into its workbooks.
 */
export function chartCreatorAxisScale(values: readonly number[]): ValueAxisScale | undefined {
  if (values.length === 0) return undefined;
  let low = Infinity;
  let high = -Infinity;
  for (const value of values) {
    if (value < low) low = value;
    if (value > high) high = value;
  }
  const target = (high - low) / 10;
  if (!(target > 0)) return undefined;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const step = [1, 2, 5, 10]
    .map((multiple) => multiple * magnitude)
    .find((candidate) => candidate >= target * (1 - 1e-9));
  if (!step) return undefined;
  const round = (value: number) => Number(value.toPrecision(12));
  return {
    min: round(Math.floor(low / step + 1e-9) * step),
    max: round(Math.ceil(high / step - 1e-9) * step),
    step: round(step),
  };
}

function sameBounds(left: ValueAxisBounds, right: ValueAxisBounds): boolean {
  const tolerance = Math.max(Math.abs(left.max - left.min), 1) * 1e-9;
  return Math.abs(left.min - right.min) <= tolerance && Math.abs(left.max - right.max) <= tolerance;
}

function boundsOf({ min, max }: ValueAxisBounds): ValueAxisBounds {
  return { min, max };
}

/** Labels with as many decimals as the step needs: 5 → "#,##0", 0.5 → "#,##0.0". */
function stepFormat(step: number): string {
  const decimals = Math.max(0, -Math.floor(Math.log10(step) + 1e-9));
  return decimals > 0 ? `#,##0.${"0".repeat(decimals)}` : "#,##0";
}

/** majorUnit follows crossBetween, crosses or crossAx, whichever the axis has last. */
function withMajorUnit(axisXml: string, step: number): string {
  if (readValue(axisXml, "majorUnit") !== undefined) {
    return axisXml.replace(/<c:majorUnit val="[^"]*"\s*\/>/, `<c:majorUnit val="${step}"/>`);
  }
  for (const anchor of [/<c:crossBetween\b[^>]*\/>/, /<c:crosses(?:At)?\b[^>]*\/>/, /<c:crossAx\b[^>]*\/>/]) {
    const match = anchor.exec(axisXml);
    if (!match) continue;
    const at = match.index + match[0].length;
    return `${axisXml.slice(0, at)}<c:majorUnit val="${step}"/>${axisXml.slice(at)}`;
  }
  return axisXml;
}

/**
 * Writes a fitted scale. A fresh fit also sets the step as the major unit and
 * swaps a plain number format linked to the cells for the step's decimals.
 * Refitting over a previous fit moves only a unit and format that fit wrote.
 */
function withScale(axisXml: string, scale: ValueAxisScale, previous?: ValueAxisScale): string {
  let xml = axisXml
    .replace(/<c:min val="[^"]*"\s*\/>/, `<c:min val="${scale.min}"/>`)
    .replace(/<c:max val="[^"]*"\s*\/>/, `<c:max val="${scale.max}"/>`);
  const unit = readValue(xml, "majorUnit");
  if (!previous || (unit !== undefined && Math.abs(Number(unit) - previous.step) <= previous.step * 1e-9)) {
    xml = withMajorUnit(xml, scale.step);
  }
  return xml.replace(/<c:numFmt\b([^>]*?)\s*\/>/, (numFmt, attributes: string) => {
    const code = readAttribute(attributes, "formatCode");
    const linked = readAttribute(attributes, "sourceLinked") === "1";
    const replaceable = previous
      ? !linked && code === stepFormat(previous.step)
      : linked && PLAIN_NUMBER_FORMAT.test(code);
    return replaceable ? `<c:numFmt formatCode="${stepFormat(scale.step)}" sourceLinked="0"/>` : numFmt;
  });
}

/** True when the axis carries only lines or scatter points, which need no zero baseline. */
function linesOnly(chartXml: string, axisId: string): boolean {
  let found = false;
  for (const group of chartXml.matchAll(CHART_GROUP)) {
    const groupAxisIds = Array.from((group[2] ?? "").matchAll(/<c:axId val="([^"]*)"/g), (match) => match[1]);
    if (!groupAxisIds.includes(axisId)) continue;
    if (!LINE_GROUPS.has(group[1] ?? "")) return false;
    found = true;
  }
  return found;
}

export function valueAxes(chartXml: string): ValueAxis[] {
  return Array.from(chartXml.matchAll(/<c:valAx>[\s\S]*?<\/c:valAx>/g), (match) => {
    const xml = match[0];
    const min = Number(readValue(xml, "min"));
    const max = Number(readValue(xml, "max"));
    const fixed =
      readValue(xml, "min") !== undefined &&
      readValue(xml, "max") !== undefined &&
      Number.isFinite(min) &&
      Number.isFinite(max) &&
      max > min;
    return {
      id: readValue(xml, "axId") ?? "",
      vertical: /^[lr]$/.test(readValue(xml, "axPos") ?? ""),
      ...(fixed ? { bounds: { min, max } } : {}),
      start: match.index ?? 0,
      end: (match.index ?? 0) + xml.length,
      xml,
    };
  });
}

/** Reads the cached cell values that chart series formulas point at. */
export class WorkbookCells {
  private readonly cellsBySheet = new Map<string, Promise<Cell[] | undefined>>();
  private names?: Promise<{ definedNames: Map<string, string>; sheetPaths: Map<string, string> }>;

  constructor(private readonly zip: MutableZip) {}

  /**
   * The numbers a series reference covers. With the series' category
   * reference, only as many leading rows as the categories fill count, which
   * drops the zeros ChartCreator's array formulas cache below the last date.
   */
  async numbersIn(reference: string, categories?: string): Promise<number[] | undefined> {
    const area = await this.area(reference);
    const cells = area && (await this.sheetCells(area.sheet));
    if (!area || !cells) return undefined;
    let covered = cells.filter((cell) => contains(area, cell));
    if (categories) {
      const categoryArea = await this.area(categories);
      const categoryCells = categoryArea && (await this.sheetCells(categoryArea.sheet));
      if (!categoryArea || !categoryCells) return undefined;
      const filled = categoryCells.filter((cell) => contains(categoryArea, cell)).length;
      const byRow = area.lastRow > area.firstRow;
      covered = covered.filter(
        (cell) => (byRow ? cell.row - area.firstRow : cell.column - area.firstColumn) < filled,
      );
    }
    return covered.filter((cell) => Number.isFinite(cell.value)).map((cell) => cell.value);
  }

  private async area(reference: string): Promise<Area | undefined> {
    const { definedNames } = await this.workbook();
    const name = reference.replace(/^\[0\]!/, "");
    const formula = definedNames.get(name) ?? reference;
    // OFFSET(Sheet!$I$41,,,COUNTA(Sheet!$I$41:$I$3044)) spans its COUNTA range.
    const areas = Array.from(formula.matchAll(AREA_REFERENCE), (match): Area => {
      const firstRow = Number(match[4]);
      const lastRow = match[6] ? Number(match[6]) : firstRow;
      return {
        sheet: match[1]?.replace(/''/g, "'") ?? match[2] ?? "",
        firstColumn: columnNumber(match[3]),
        lastColumn: columnNumber(match[5] ?? match[3]),
        firstRow,
        lastRow,
      };
    });
    if (areas.length === 0) return undefined;
    return areas.reduce((widest, candidate) =>
      candidate.lastRow - candidate.firstRow > widest.lastRow - widest.firstRow ? candidate : widest,
    );
  }

  private workbook() {
    this.names ??= (async () => {
      const workbookXml = (await this.zip.file("xl/workbook.xml")?.async("string")) ?? "";
      const relsXml = (await this.zip.file("xl/_rels/workbook.xml.rels")?.async("string")) ?? "";
      const targets = new Map<string, string>();
      for (const match of relsXml.matchAll(/<Relationship\b([^>]*)\/?>/gi)) {
        targets.set(readAttribute(match[1] ?? "", "Id"), readAttribute(match[1] ?? "", "Target"));
      }
      const sheetPaths = new Map<string, string>();
      for (const match of workbookXml.matchAll(/<sheet\b([^>]*)\/?>/gi)) {
        const target = targets.get(readAttribute(match[1] ?? "", "r:id"));
        if (target) {
          sheetPaths.set(readAttribute(match[1] ?? "", "name"), resolvePartPath("xl/workbook.xml", target));
        }
      }
      const definedNames = new Map<string, string>();
      for (const match of workbookXml.matchAll(/<definedName\b([^>]*)>([\s\S]*?)<\/definedName>/gi)) {
        definedNames.set(readAttribute(match[1] ?? "", "name"), decodeXml(match[2] ?? ""));
      }
      return { definedNames, sheetPaths };
    })();
    return this.names;
  }

  private sheetCells(sheetName: string): Promise<Cell[] | undefined> {
    let pending = this.cellsBySheet.get(sheetName);
    if (!pending) {
      pending = (async () => {
        const path = (await this.workbook()).sheetPaths.get(sheetName);
        const xml = path ? await this.zip.file(path)?.async("string") : undefined;
        if (!xml) return undefined;
        const cells: Cell[] = [];
        for (const match of xml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
          const attributes = match[1] ?? "";
          const address = /^([A-Z]{1,3})(\d+)$/.exec(readAttribute(attributes, "r"));
          const raw = /<v>([^<]*)<\/v>/.exec(match[2] ?? "")?.[1];
          if (!address || !raw) continue;
          const numeric = !NON_NUMERIC_CELL_TYPES.has(readAttribute(attributes, "t"));
          cells.push({ column: columnNumber(address[1]), row: Number(address[2]), value: numeric ? Number(raw) : NaN });
        }
        return cells;
      })();
      this.cellsBySheet.set(sheetName, pending);
    }
    return pending;
  }
}

function seriesFormula(seriesXml: string, block: RegExp): string | undefined {
  const content = block.exec(seriesXml)?.[2];
  const formula = content ? /<c:f>([^<]*)<\/c:f>/.exec(content)?.[1] : undefined;
  return formula === undefined ? undefined : decodeXml(formula);
}

/**
 * Every value in the series against the axis, or undefined if any series
 * cannot be read. `plotted` keeps only rows the series' categories fill.
 */
async function axisValues(
  chartXml: string,
  axisId: string,
  cells: WorkbookCells,
  plotted: boolean,
): Promise<number[] | undefined> {
  const values: number[] = [];
  for (const group of chartXml.matchAll(CHART_GROUP)) {
    const body = group[2] ?? "";
    const groupAxisIds = Array.from(body.matchAll(/<c:axId val="([^"]*)"/g), (match) => match[1]);
    if (!groupAxisIds.includes(axisId)) continue;
    for (const series of body.matchAll(/<c:ser>([\s\S]*?)<\/c:ser>/g)) {
      const seriesXml = series[1] ?? "";
      const formula = seriesFormula(seriesXml, /<c:(val|yVal)>([\s\S]*?)<\/c:\1>/);
      if (!formula) return undefined;
      const categories = plotted ? seriesFormula(seriesXml, /<c:(cat|xVal)>([\s\S]*?)<\/c:\1>/) : undefined;
      const numbers = await cells.numbersIn(formula, categories);
      if (!numbers) return undefined;
      values.push(...numbers);
    }
  }
  return values.length > 0 ? values : undefined;
}

async function axisScale(
  chartXml: string,
  axisId: string,
  cells: WorkbookCells,
  plotted: boolean,
): Promise<ValueAxisScale | undefined> {
  return chartCreatorAxisScale((await axisValues(chartXml, axisId, cells, plotted)) ?? []);
}

/**
 * ChartCreator's Excel export applies its axis rule to whole template columns,
 * so the zeros its array formulas cache below the last date pull a line
 * chart's value axis down to 0. ChartCreator's own renders apply the rule to
 * the plotted rows only; this does the same, with the rule's step as the major
 * unit. Axes that also carry bars or areas keep their zero baseline, and
 * bounds that do not match the rule are left as set.
 */
export async function fitChartCreatorValueAxes(
  workbookBase64: string,
): Promise<{ workbookBase64: string; rescaled: RescaledValueAxis[] }> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  const cells = new WorkbookCells(zip);
  const rescaled: RescaledValueAxis[] = [];
  let changed = false;

  for (const chartPath of Object.keys(zip.files).filter((path) => CHART_PART.test(path))) {
    const chartXml = await zip.file(chartPath)?.async("string");
    if (!chartXml) continue;
    const axes = valueAxes(chartXml);
    let updated = chartXml;
    // Splice from the last axis back so earlier offsets stay valid.
    for (let index = axes.length - 1; index >= 0; index -= 1) {
      const axis = axes[index];
      if (!axis.vertical || !axis.bounds || !linesOnly(chartXml, axis.id)) continue;
      const whole = await axisScale(chartXml, axis.id, cells, false);
      if (!whole || !sameBounds(whole, axis.bounds)) continue;
      const plotted = await axisScale(chartXml, axis.id, cells, true);
      if (!plotted) continue;
      updated = updated.slice(0, axis.start) + withScale(axis.xml, plotted) + updated.slice(axis.end);
      if (!sameBounds(plotted, axis.bounds)) {
        rescaled.push({ chartPath, from: axis.bounds, to: boundsOf(plotted) });
      }
    }
    if (updated === chartXml) continue;
    zip.file(chartPath, updated);
    changed = true;
  }

  return {
    workbookBase64: changed ? await zip.generateAsync({ type: "base64", compression: "DEFLATE" }) : workbookBase64,
    rescaled,
  };
}

/**
 * ChartCreator fixes each value axis to bounds derived from the data it
 * downloaded, and a Preslink refresh keeps them after swapping in other data,
 * which clips the new series. Re-derives those bounds from the refreshed cells.
 * Only axes whose bounds still match the rule on the source data, over the
 * plotted rows or the whole columns, are touched, so hand-set or unrecognised
 * bounds survive.
 */
export async function fitRefreshedValueAxes(
  sourceWorkbookBase64: string,
  refreshedWorkbookBase64: string,
): Promise<{ workbookBase64: string; rescaled: RescaledValueAxis[] }> {
  const [source, refreshed] = await Promise.all([
    JSZip.loadAsync(sourceWorkbookBase64, { base64: true }),
    JSZip.loadAsync(refreshedWorkbookBase64, { base64: true }),
  ]);
  const sourceCells = new WorkbookCells(source);
  const refreshedCells = new WorkbookCells(refreshed);
  const rescaled: RescaledValueAxis[] = [];

  for (const chartPath of Object.keys(refreshed.files).filter((path) => CHART_PART.test(path))) {
    const refreshedXml = await refreshed.file(chartPath)?.async("string");
    const sourceXml = await source.file(chartPath)?.async("string");
    if (!refreshedXml || !sourceXml) continue;
    const sourceAxes = valueAxes(sourceXml);
    const refreshedAxes = valueAxes(refreshedXml);
    if (sourceAxes.length !== refreshedAxes.length) continue;

    let updated = refreshedXml;
    // Splice from the last axis back so earlier offsets stay valid.
    for (let index = refreshedAxes.length - 1; index >= 0; index -= 1) {
      const before = sourceAxes[index];
      const after = refreshedAxes[index];
      if (!before.vertical || !after.vertical || !before.bounds || !after.bounds) continue;
      const sourceBounds = before.bounds;
      let derived: ValueAxisScale | undefined;
      let plotted = false;
      for (const candidate of [true, false]) {
        const scale = await axisScale(sourceXml, before.id, sourceCells, candidate);
        if (scale && sameBounds(scale, sourceBounds)) {
          derived = scale;
          plotted = candidate;
          break;
        }
      }
      if (!derived) continue;
      const fitted = await axisScale(refreshedXml, after.id, refreshedCells, plotted);
      if (!fitted || sameBounds(fitted, after.bounds)) continue;
      updated = updated.slice(0, after.start) + withScale(after.xml, fitted, derived) + updated.slice(after.end);
      rescaled.push({ chartPath, from: after.bounds, to: boundsOf(fitted) });
    }
    if (updated !== refreshedXml) refreshed.file(chartPath, updated);
  }

  if (rescaled.length === 0) {
    return { workbookBase64: refreshedWorkbookBase64, rescaled };
  }
  return {
    workbookBase64: await refreshed.generateAsync({ type: "base64", compression: "DEFLATE" }),
    rescaled,
  };
}
